import { inject, Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';

type ErrorPattern = {
  regex: RegExp;
  key: string;
};

const ERROR_PATTERNS: ErrorPattern[] = [
  // Inbound receive – serial already exists in system
  {
    regex: /^Serial numbers already exist in the system:\s*(?<serial>.+?)\s*\.?\s*Please use different serial numbers\.?\s*$/i,
    key: 'inbound.receive.serial_numbers_already_exist',
  },
  // Inventory/Batch
  {
    regex: /^Batch already exists with id (?<value>.+)/i,
    key: 'batch_management.messages.upsert.already_exist',
  },
  // Stock/Zone
  {
    regex: /^Zone with name (?<name>.+) already exists/i,
    key: 'zone.errors.zone_existed',
  },
  {
    regex: /^Zone with name (?<name>.+) already exists in this warehouse\./i,
    key: 'zone.errors.zone_existed_in_warehouse',
  },
  {
    regex: /^Aisle with name (?<name>.+) already exists in this zone/i,
    key: 'zone.errors.aisle_existed',
  },
  {
    regex: /^Aisle with name (?<name>.+) already exists in this warehouse\./i,
    key: 'zone.errors.aisle_existed_in_warehouse',
  },
  {
    regex: /^Rack with name (?<name>.+) already exists in this aisle/i,
    key: 'zone.errors.rack_existed',
  },
  {
    regex: /^Rack with name (?<name>.+) already exists in this warehouse\./i,
    key: 'zone.errors.rack_existed_in_warehouse',
  },
  {
    regex: /^Shelf with name (?<name>.+) already exists in this rack/i,
    key: 'zone.errors.shelf_existed',
  },
  {
    regex: /^Bin with name (?<name>.+) already exists in this shelf/i,
    key: 'zone.errors.bin_existed',
  },
  {
    regex: /^Bin with name (?<name>.+) already exists in this rack\./i,
    key: 'zone.errors.bin_existed_in_rack',
  },
  {
    regex: /^Aisle name must be unique within a zone/i,
    key: 'zone.errors.aisle_duplicate',
  },
  {
    regex: /^Aisle name must be unique in the warehouse\./i,
    key: 'zone.errors.aisle_unique_in_warehouse',
  },
  {
    regex: /^Rack name must be unique within an aisle/i,
    key: 'zone.errors.rack_duplicate',
  },
  {
    regex: /^Rack name must be unique in the warehouse\./i,
    key: 'zone.errors.rack_unique_in_warehouse',
  },
  {
    regex: /^Shelf name must be unique within a rack/i,
    key: 'zone.errors.shelf_duplicate',
  },
  {
    regex: /^Bin name must be unique within a shelf/i,
    key: 'zone.errors.bin_duplicate',
  },
  {
    regex: /^Bin name must be unique in a rack\./i,
    key: 'zone.errors.bin_unique_in_rack',
  },
  {
    regex: /^Zone name must be unique within a warehouse\./i,
    key: 'zone.errors.zone_unique_in_warehouse',
  },
  {
    regex: /^Zone name must be unique within the tenant\./i,
    key: 'zone.errors.zone_unique_in_tenant',
  },
  {
    regex: /^Total width of zones exceeds width of warehouse/i,
    key: 'zone.errors.zones_width_exceed_warehouse',
  },
  {
    regex: /^Total width of aisles exceeds width of warehouse/i,
    key: 'zone.errors.aisles_width_exceed_warehouse',
  },
  {
    regex: /^Length of zone exceeds length of warehouse/i,
    key: 'zone.errors.zone_length_exceed_warehouse',
  },
  {
    regex:
      /^Total width of aisles exceeds width of zone for zone: (?<name>.+)/i,
    key: 'zone.errors.aisles_width_exceed_zone',
  },
  {
    regex: /^Length of aisle exceeds length of warehouse/i,
    key: 'zone.errors.aisle_length_exceed_warehouse',
  },
  {
    regex: /^Length of aisle exceeds length of zone for zone: (?<name>.+)/i,
    key: 'zone.errors.aisle_length_exceed_zone',
  },
  {
    regex: /^Length of rack exceeds length of warehouse/i,
    key: 'zone.errors.rack_length_exceed_warehouse',
  },
  {
    regex: /^Length of rack exceeds length of aisle for aisle: (?<name>.+)/i,
    key: 'zone.errors.rack_length_exceed_aisle',
  },
  {
    regex: /^Length of rack exceeds length of zone for zone: (?<name>.+)/i,
    key: 'zone.errors.rack_length_exceed_zone',
  },
  {
    regex: /^Total width of racks exceeds width of warehouse/i,
    key: 'zone.errors.racks_width_exceed_warehouse',
  },
  {
    regex: /^Total width of racks exceeds width of zone for zone: (?<name>.+)/i,
    key: 'zone.errors.racks_width_exceed_zone',
  },
  {
    regex:
      /^Total width of racks exceeds width of aisle for aisle: (?<name>.+)/i,
    key: 'zone.errors.racks_width_exceed_aisle',
  },
  {
    regex: /^Total volume of bins exceeds rack volume for rack: (?<name>.+)/i,
    key: 'zone.errors.bins_volume_exceed_rack',
  },
  {
    regex:
      /^Total weight of items in bin exceeds rack weight limit for rack: (?<name>.+)/i,
    key: 'zone.errors.bin_weight_exceed_rack',
  },
  {
    regex:
      /^Total weight of items in bin exceeds weight limit for rack: (?<name>.+)/i,
    key: 'zone.errors.bin_weight_exceed_rack_limit',
  },
  {
    regex:
      /^Total height of shelves exceeds rack height for rack: (?<name>.+)/i,
    key: 'zone.errors.shelf_capacity_exceed_rack',
  },
  {
    regex: /^Total volume of bins exceeds shelf volume for shelf: (?<name>.+)/i,
    key: 'zone.errors.bin_capacity_exceed_shelf',
  },
  {
    regex: /^Total volume of bins exceeds shelf volume for shelf:(?<name>.+)/i,
    key: 'zone.errors.bin_capacity_exceed_shelf',
  },
  {
    regex: /^Total volume of items exceeds bin volume for bin: (?<name>.+)/i,
    key: 'zone.errors.bin_item_capacity_exceed_bin',
  },
  {
    regex:
      /^Total weight of items in bin exceeds shelf weight limit for shelf: (?<name>.+)/i,
    key: 'zone.errors.bin_item_capacity_exceed_shelf',
  },
  {
    regex:
      /^Number of shelves exceeds the max level number for rack: (?<name>.+)/i,
    key: 'zone.errors.shelves_exceed_rack_limit',
  },
  {
    regex: /^Duplicate shelf level number: (?<no>.+) for rack: (?<name>.+)/i,
    key: 'zone.errors.duplicate_shelf_level',
  },
  {
    regex: /^At least a zone, an aisle, or a rack must be provided\./i,
    key: 'zone.errors.at_least_one_structure_required',
  },
  {
    regex: /^Each zone must contain at least an aisle or a rack\./i,
    key: 'zone.errors.zone_must_contain_aisle_or_rack',
  },
  {
    regex: /^Each rack must contain at least a shelf or a bin\./i,
    key: 'zone.errors.rack_must_contain_shelf_or_bin',
  },
  {
    regex: /^Zone not found with id: (?<id>.+)/i,
    key: 'zone.errors.zone_not_found',
  },
  {
    regex: /^Aisle not found with id: (?<id>.+)/i,
    key: 'zone.errors.aisle_not_found',
  },
  {
    regex: /^Rack not found with id: (?<id>.+)/i,
    key: 'zone.errors.rack_not_found',
  },
  {
    regex: /^Shelf not found with id: (?<id>.+)/i,
    key: 'zone.errors.shelf_not_found',
  },
  {
    regex: /^Bin not found with id: (?<id>.+)/i,
    key: 'zone.errors.bin_not_found',
  },
  {
    regex: /^BinItem not found with id: (?<id>.+)/i,
    key: 'zone.errors.bin_item_not_found',
  },
  // Inventory/Inbound/Good Receive
  {
    regex:
      /^Duplicate serial numbers found in request: (?<values>.+(,\s.+)*)\. Each serial number must be unique within the request/i,
    key: 'inbound.receive.duplicate_serial_numbers',
  },
  {
    regex: /^Inbound notice already has a warehouse warrant/i,
    key: 'inbound.messages.inbound_notice_already_has_warrant',
  },
  // Warehouse/Upsert
  {
    regex: /^Warehouse code \'(?<value>.+)\' already exists/i,
    key: 'logical_warehouse.messages.error.warehouse_code_existed',
  },
  {
    regex: /^Warehouse name already exists/i,
    key: 'logical_warehouse.messages.error.warehouse_name_existed',
  },
  {
    regex: /^Warehouse name \'(?<value>.+)\' already exists/i,
    key: 'logical_warehouse.messages.error.warehouse_name_existed_detail',
  },
  {
    regex: /Min threshold or max threshold is required./i,
    key: 'alert_settings.messages.threshold_required',
  },
  {
    regex:
      /^Alert settings already existed with SKU (?<sku>.+) and warehouse (?<warehouse>.+)\./i,
    key: 'alert_settings.messages.alert_settings_already_existed',
  },
  // Procurement
  {
    regex: /Invalid warehouse code/i,
    key: 'purchase_request.messages.invalid_warehouse_code',
  },
  // Transfer Order / Logic Transfer
  {
    regex: /^SKU is Inactive or unavailable:\s*(?<skuCodes>.+)$/i,
    key: 'common.messages.sku_inactive_or_unavailable',
  },
  {
    regex:
      /^Insufficient stock for SKU (?<skuCode>\S+) in warehouse (?<warehouseCode>\S+)\. Available: (?<available>[\d.]+), Required: (?<required>[\d.]+)$/i,
    key: 'logic_transfer.messages.insufficient_stock',
  },
  {
    regex:
      /^Concurrent modification while reserving SKU (?<skuCode>\S+) in warehouse (?<warehouseId>\S+)$/i,
    key: 'logic_transfer.messages.concurrent_modification_while_reserving',
  },
  {
    regex:
      /^Concurrent modification detected for SKU (?<skuCode>\S+) in warehouse (?<warehouseId>\S+)$/i,
    key: 'logic_transfer.messages.concurrent_modification_while_reserving',
  },
  // Company/Sales Organization
  {
    regex: /Cannot delete sales organization with existing shops/i,
    key: 'organizational.company.messages.error.cannot_delete_with_shops',
  },
  // Logistics
  {
    regex: /^The provider account with code (?<code>\S+) already exists/i,
    key: 'carrier.messages.error.account_code_exists',
  },
  {
    regex: /^Account with ID (?<id>.+) not found/i,
    key: 'carrier.messages.error.id_not_found',
  },
  {
    regex: /^Invalid username or password/i,
    key: 'carrier.messages.error.invalid_username_password',
  },
  {
    regex: /^The account with user ID: (.+) already exist/i,
    key: 'carrier.messages.error.user_id_exists',
  },
];

@Injectable({ providedIn: 'root' })
export class ErrorTranslatorService {
  private readonly translator = inject(TranslateService);

  translate(error: string, fallbackKey = 'common.messages.error'): string {
    const fallbackMessage = this.translator.instant(fallbackKey);
    if (!error) return fallbackMessage;

    for (const err of ERROR_PATTERNS) {
      const matched = err.regex.exec(error);
      if (!matched) continue;
      return this.translator.instant(err.key, matched.groups ?? {});
    }

    return fallbackMessage;
  }
}
